import { useCallback, useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

const Track = styled.div`
  height: 6px;
  border-radius: 100px;
`;

const Bar = styled.div`
  position: relative;
  background-color: ${(props) => props.theme.global.mainColor};
  height: 100%;
  border-radius: 100px;
`;

const Button = styled.button`
  position: absolute;
  top: 50%;
  right: 0;
  width: 24px;
  height: 24px;
  border-radius: 100%;
  box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
  transform: translate(50%, -50%);

  &:disabled {
    cursor: default;
  }
`;

const Text = styled.span`
  position: absolute;
  top: 20px;
  right: 0px;
  transform: translate(50%);
  cursor: default;
  font-size: 12px;
`;

const Container = styled.div<{ disabled?: boolean }>`
  ${Track} {
    background-color: ${(props) =>
      props.disabled ? props.theme.ui.button.disabledBgColor : `${props.theme.global.mainColor}80`};
  }

  ${Bar}, ${Button} {
    background-color: ${(props) =>
      props.disabled ? props.theme.ui.button.disabledBgColor : props.theme.global.mainColor};
  }
`;

interface SliderProps {
  ratio: number;
  min: number;
  max: number;
  disabled?: boolean;
  onRatioChange: (newRatio: number) => void;
}

const Slider = ({ ratio, min, max, disabled = false, onRatioChange }: SliderProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [startInfo, setStartInfo] = useState<{ pos: number; ratio: number } | undefined>();

  const onClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const onMouseDown = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;

      const { clientX } = event;
      setStartInfo({
        pos: clientX,
        ratio,
      });
    },
    [ratio, disabled, setStartInfo]
  );

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (disabled || !ref.current || !startInfo) return;

      const { clientX } = event;
      const sliderWidth = ref.current.clientWidth;
      const pixelWidth = sliderWidth / 100;

      const diff = clientX - startInfo.pos;
      const newRatio = startInfo.ratio + diff / pixelWidth / 100;
      const validNewRatio = newRatio >= max ? max : newRatio <= min ? min : newRatio;

      onRatioChange(Math.round(validNewRatio * 100) / 100);
    };

    const handleMouseUp = () => {
      if (disabled) return;

      setStartInfo(undefined);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mouseleave', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [ref, startInfo, disabled, min, max, setStartInfo, onRatioChange]);

  return (
    <Container ref={ref} disabled={disabled}>
      <Track>
        <Bar style={{ width: `${ratio * 100}%` }}>
          <Button onClick={onClick} onMouseDown={onMouseDown} disabled={disabled}></Button>
          <Text>{`${Math.round(ratio * 100)}%`}</Text>
        </Bar>
      </Track>
    </Container>
  );
};

export default Slider;
